import { useState, useCallback, useEffect } from 'react';
import { Upload, FileText, CheckCircle, AlertCircle, X, ChevronDown, ChevronUp, TrendingUp } from 'lucide-react';
import api, { fetchApi } from '../api/client';

export default function CsvUpload({ onImportComplete }) {
  const [file, setFile] = useState(null);
  const [csvContent, setCsvContent] = useState('');
  const [format, setFormat] = useState('fidelity_positions');
  const [selectedAccount, setSelectedAccount] = useState('');
  const [accounts, setAccounts] = useState([]);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showChanges, setShowChanges] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  // Fetch accounts from API
  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const data = await fetchApi('/accounts');
        const activeAccounts = data.filter(a => !a.is_closed);
        setAccounts(activeAccounts);
        // Set default account if we have accounts
        if (activeAccounts.length > 0) {
          // Prefer fidelity_brokeragelink as default, or first account
          const defaultAccount = activeAccounts.find(a => a.id === 'fidelity_brokeragelink') 
              || activeAccounts[0];
          setSelectedAccount(defaultAccount.id);
        }
      } catch (err) {
        console.error('Error fetching accounts:', err);
      }
    };
    fetchAccounts();
  }, []);

  const formats = [
    { id: 'fidelity_positions', name: 'Fidelity Positions', description: 'Fidelity Positions CSV export' },
    { id: 'fidelity_trades', name: 'Fidelity Trades', description: 'Fidelity History/Activity CSV export' },
    { id: 'fidelity_realized_gains', name: 'Fidelity Realized Gains', description: 'Close tax lots & record gains (per-lot export)', requiresAccount: true },
    { id: 'ameriprise_positions', name: 'Ameriprise Positions', description: 'Ameriprise Portfolio Positions CSV export' },
    { id: 'schwab_positions', name: 'Schwab Positions', description: 'Charles Schwab Positions CSV export' },
  ];

  const isRealizedGainsFormat = format === 'fidelity_realized_gains';

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.type === 'text/csv') {
      handleFile(droppedFile);
    } else {
      setError('Please upload a CSV file');
    }
  }, []);

  const handleFileInput = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      handleFile(selectedFile);
    }
  };

  const handleFile = (file) => {
    setFile(file);
    setError(null);
    setPreview(null);
    setResult(null);
    
    const reader = new FileReader();
    reader.onload = (e) => {
      setCsvContent(e.target.result);
    };
    reader.onerror = () => {
      setError('Error reading file');
    };
    reader.readAsText(file);
  };

  const handlePreview = async () => {
    if (!csvContent) {
      setError('No file selected');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      if (isRealizedGainsFormat) {
        // Use realized-gains/preview endpoint (does NOT import)
        const data = await fetchApi('/lots/realized-gains/preview', {
          method: 'POST',
          body: JSON.stringify({
            content: csvContent,
            account_id: selectedAccount,
            create_ghost_lots: true,
          }),
        });
        if (data.success === false) {
          throw new Error(data.error || 'Failed to preview realized gains');
        }
        // Show preview (NOT imported yet)
        setPreview({
          imported: data.imported,  // Count of NEW items to import
          skipped: data.skipped,    // Count of duplicates
          total_gain_loss: data.total_gain_loss,
          accounts: data.accounts,
          items: data.items,        // Detailed list for display
          isImportResult: false,    // Important: this is just a preview
        });
        setResult(null);  // Clear any previous result
      } else {
        // Pass account ID for per-account exports (fidelity_trades History format)
        const accountId = format === 'fidelity_trades' ? selectedAccount : null;
        const previewResult = await api.previewImport(csvContent, format, accountId);
        setPreview(previewResult);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!csvContent) {
      setError('No file selected');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      if (isRealizedGainsFormat) {
        // Now actually import (preview was shown first)
        const data = await fetchApi('/lots/realized-gains/import', {
          method: 'POST',
          body: JSON.stringify({
            content: csvContent,
            account_id: selectedAccount,
            create_ghost_lots: true,
          }),
        });
        if (data.success === false) {
          throw new Error(data.error || 'Failed to import realized gains');
        }
        setResult({
          status: 'success',
          success: true,
          message: `Imported ${data.imported} realized gains (${data.skipped} skipped as duplicates)`,
          imported: data.imported,
          skipped: data.skipped,
          total_gain_loss: data.total_gain_loss,
        });
        setPreview(null);
        if (onImportComplete) onImportComplete();
      } else {
        // Pass account ID for per-account exports (fidelity_trades History format)
        const accountId = format === 'fidelity_trades' ? selectedAccount : null;
        const importResult = await api.importCsv(csvContent, format, false, accountId);
        setResult(importResult);
        setPreview(null);
        
        if (importResult.status === 'success' && onImportComplete) {
          onImportComplete();
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setCsvContent('');
    setPreview(null);
    setResult(null);
    setError(null);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Format Selection */}
      <div className="card">
        <h3 className="card-header">
          <FileText className="w-5 h-5 text-sky-400" />
          Select Import Format
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {formats.map((f) => (
            <button
              key={f.id}
              onClick={() => setFormat(f.id)}
              className={`p-4 rounded-lg border-2 text-left transition-all ${
                format === f.id
                  ? f.id === 'fidelity_realized_gains' 
                    ? 'border-emerald-500 bg-emerald-500/10'
                    : 'border-sky-500 bg-sky-500/10'
                  : 'border-slate-600 hover:border-slate-500 bg-slate-800/50'
              }`}
            >
              <div className="flex items-center gap-2">
                {f.id === 'fidelity_realized_gains' && <TrendingUp className="w-4 h-4 text-emerald-400" />}
                <p className="font-medium text-slate-200">{f.name}</p>
              </div>
              <p className="text-sm text-slate-400 mt-1">{f.description}</p>
            </button>
          ))}
        </div>
        
        {/* Account Selector for Realized Gains */}
        {isRealizedGainsFormat && (
          <div className="mt-4 p-4 bg-emerald-900/20 border border-emerald-700/30 rounded-lg">
            <label className="block text-sm text-emerald-300 mb-2">
              Select Account for Lot Matching
            </label>
            <select
              value={selectedAccount}
              onChange={(e) => setSelectedAccount(e.target.value)}
              className="w-full md:w-64 bg-slate-900 border border-slate-600 rounded-lg px-4 py-2 text-white"
            >
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
            <p className="text-xs text-slate-400 mt-2">
              ℹ️ Export per-lot "Realized Gains" from Fidelity (with Date Acquired/Sold columns). 
              This will close matching tax lots and record realized G/L.
            </p>
          </div>
        )}
        
        {/* Account Selector for Fidelity Trades (per-account History export) */}
        {format === 'fidelity_trades' && (
          <div className="mt-4 p-4 bg-sky-900/20 border border-sky-700/30 rounded-lg">
            <label className="block text-sm text-sky-300 mb-2">
              Select Account (for per-account History exports)
            </label>
            <select
              value={selectedAccount}
              onChange={(e) => setSelectedAccount(e.target.value)}
              className="w-full md:w-64 bg-slate-900 border border-slate-600 rounded-lg px-4 py-2 text-white"
            >
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
            <p className="text-xs text-slate-400 mt-2">
              ℹ️ Select the account if importing a per-account "History" export. 
              For multi-account "Activity" exports (with Account column), this will be auto-detected.
            </p>
          </div>
        )}
      </div>

      {/* File Upload */}
      <div className="card">
        <h3 className="card-header">
          <Upload className="w-5 h-5 text-sky-400" />
          Upload CSV File
        </h3>
        
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-all ${
            dragOver
              ? 'border-sky-500 bg-sky-500/10'
              : file
              ? 'border-emerald-500 bg-emerald-500/10'
              : 'border-slate-600 hover:border-slate-500'
          }`}
        >
          {file ? (
            <div className="flex items-center justify-center gap-4">
              <CheckCircle className="w-8 h-8 text-emerald-400" />
              <div className="text-left">
                <p className="font-medium text-slate-200">{file.name}</p>
                <p className="text-sm text-slate-400">{(file.size / 1024).toFixed(1)} KB</p>
              </div>
              <button
                onClick={handleReset}
                className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>
          ) : (
            <>
              <Upload className="w-12 h-12 text-slate-500 mx-auto mb-4" />
              <p className="text-slate-300 mb-2">Drag and drop your CSV file here</p>
              <p className="text-slate-500 text-sm mb-4">or</p>
              <label className="btn btn-primary cursor-pointer">
                Browse Files
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileInput}
                  className="hidden"
                />
              </label>
            </>
          )}
        </div>

        {/* Actions */}
        {file && (
          <div className="flex gap-4 mt-4">
            <button
              onClick={handlePreview}
              disabled={loading}
              className="btn btn-secondary"
            >
              {loading ? <div className="spinner" /> : 'Preview Changes'}
            </button>
            <button
              onClick={handleImport}
              disabled={loading || !preview}
              className="btn btn-primary"
            >
              {loading ? <div className="spinner" /> : 'Import Data'}
            </button>
          </div>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="p-4 bg-red-500/20 border border-red-500/30 rounded-xl flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-red-400">Import Error</p>
            <p className="text-sm text-red-300 mt-1">{error}</p>
          </div>
        </div>
      )}

      {/* Preview */}
      {preview && (
        <div className="card">
          <h3 className="card-header">
            <FileText className="w-5 h-5 text-amber-400" />
            Import Preview
          </h3>
          
          {/* REALIZED GAINS PREVIEW/RESULT */}
          {isRealizedGainsFormat ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 bg-emerald-900/30 border border-emerald-700/50 rounded-lg">
                  <p className="text-2xl font-bold text-emerald-400">{preview.imported || 0}</p>
                  <p className="text-sm text-slate-400">Imported</p>
                </div>
                <div className="p-4 bg-slate-700/50 rounded-lg">
                  <p className="text-2xl font-bold text-slate-400">{preview.skipped || 0}</p>
                  <p className="text-sm text-slate-400">Skipped (duplicates)</p>
                </div>
                <div className={`p-4 rounded-lg ${(preview.total_gain_loss || 0) >= 0 ? 'bg-emerald-900/30 border border-emerald-700/50' : 'bg-red-900/30 border border-red-700/50'}`}>
                  <p className={`text-2xl font-bold ${(preview.total_gain_loss || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    ${(preview.total_gain_loss || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                  </p>
                  <p className="text-sm text-slate-400">Total G/L Imported</p>
                </div>
                <div className="p-4 bg-sky-900/30 border border-sky-700/50 rounded-lg">
                  <p className="text-2xl font-bold text-sky-400">{preview.accounts?.length || 1}</p>
                  <p className="text-sm text-slate-400">Account(s)</p>
                </div>
              </div>
              
              {preview.isImportResult ? (
                <div className="p-4 bg-emerald-900/20 border border-emerald-700/30 rounded-lg">
                  <p className="text-emerald-400 font-medium">✅ Import Complete!</p>
                  <p className="text-sm text-slate-400 mt-1">
                    {preview.imported} realized gain entries have been recorded. Refresh the dashboard to see updated totals.
                  </p>
                </div>
              ) : (
                <div className="p-4 bg-amber-900/20 border border-amber-700/30 rounded-lg">
                  <p className="text-amber-400 font-medium">📋 Preview - Not Yet Imported</p>
                  <p className="text-sm text-slate-400 mt-1">
                    {preview.imported} new entries will be imported, {preview.skipped} duplicates will be skipped.
                    Click "Import Data" to proceed.
                  </p>
                </div>
              )}
              
              {preview.items && preview.items.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-slate-400 border-b border-slate-700">
                        <th className="text-left p-2">Status</th>
                        <th className="text-left p-2">Ticker</th>
                        <th className="text-left p-2">Account</th>
                        <th className="text-left p-2">Acquired</th>
                        <th className="text-left p-2">Sold</th>
                        <th className="text-right p-2">Qty</th>
                        <th className="text-right p-2">G/L</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.items.slice(0, 50).map((item, i) => (
                        <tr key={i} className={`border-b border-slate-700/50 ${item.status === 'duplicate' ? 'opacity-50' : ''}`}>
                          <td className="p-2">
                            <span className={`px-2 py-1 rounded text-xs ${
                              item.status === 'new' ? 'bg-emerald-900/50 text-emerald-300' :
                              'bg-slate-700 text-slate-400'
                            }`}>
                              {item.status === 'new' ? '+ new' : 'skip'}
                            </span>
                          </td>
                          <td className="p-2 font-medium text-white">{item.ticker}</td>
                          <td className="p-2 text-slate-400 text-xs">{item.account}</td>
                          <td className="p-2 text-slate-400">{item.acquired_date}</td>
                          <td className="p-2 text-slate-400">{item.sold_date}</td>
                          <td className="p-2 text-right text-slate-300">{item.quantity?.toFixed(3)}</td>
                          <td className={`p-2 text-right font-medium ${item.gain_loss >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                            {item.gain_loss >= 0 ? '+' : ''}${item.gain_loss?.toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {preview.items.length > 50 && (
                    <p className="text-sm text-slate-500 mt-2">...and {preview.items.length - 50} more</p>
                  )}
                </div>
              )}
              
              {preview.imported > 0 && !preview.isImportResult && (
                <p className="text-sm text-amber-300">
                  ⚠️ Click "Import Data" to import {preview.imported} realized gain entries.
                </p>
              )}
            </div>
          ) : preview.changes?.[0]?.price !== undefined || preview.changes?.some(c => c.is_contribution) ? (
            /* TRADES PREVIEW */
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-4">
              <div className="p-4 bg-slate-700/50 rounded-lg">
                <p className="text-2xl font-bold text-emerald-400">
                  {preview.changes?.filter(c => c.action === 'BUY' && c.status !== 'duplicate' && !c.is_contribution).length || 0}
                </p>
                <p className="text-sm text-slate-400">New buys</p>
              </div>
              <div className="p-4 bg-slate-700/50 rounded-lg">
                <p className="text-2xl font-bold text-red-400">
                  {preview.changes?.filter(c => c.action === 'SELL' && c.status !== 'duplicate' && !c.is_contribution).length || 0}
                </p>
                <p className="text-sm text-slate-400">New sells</p>
              </div>
              <div className="p-4 bg-sky-900/50 border border-sky-700/50 rounded-lg">
                <p className="text-2xl font-bold text-sky-400">
                  {preview.changes?.filter(c => c.is_contribution && c.status !== 'duplicate').length || 0}
                </p>
                <p className="text-sm text-slate-400">Contributions</p>
              </div>
              <div className="p-4 bg-slate-700/50 rounded-lg">
                <p className="text-2xl font-bold text-slate-400">
                  {preview.changes?.filter(c => c.status === 'duplicate').length || 0}
                </p>
                <p className="text-sm text-slate-400">Duplicates (skip)</p>
              </div>
              <div className="p-4 bg-slate-700/50 rounded-lg">
                <p className="text-2xl font-bold text-amber-400">
                  {preview.changes?.filter(c => c.status !== 'duplicate').length || 0}
                </p>
                <p className="text-sm text-slate-400">To import</p>
              </div>
              <div className="p-4 bg-slate-700/50 rounded-lg">
                <p className="text-2xl font-bold text-slate-200">{preview.accounts_detected?.length || 0}</p>
                <p className="text-sm text-slate-400">Accounts</p>
              </div>
            </div>
          ) : (
            /* POSITIONS PREVIEW */
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
              <div className="p-4 bg-slate-700/50 rounded-lg">
                <p className="text-2xl font-bold text-emerald-400">
                  {preview.changes?.filter(c => c.action === 'create').length || 0}
                </p>
                <p className="text-sm text-slate-400">New positions</p>
              </div>
              <div className="p-4 bg-slate-700/50 rounded-lg">
                <p className="text-2xl font-bold text-sky-400">
                  {preview.changes?.filter(c => c.action === 'update').length || 0}
                </p>
                <p className="text-sm text-slate-400">To update</p>
              </div>
              <div className="p-4 bg-slate-700/50 rounded-lg">
                <p className="text-2xl font-bold text-red-400">
                  {preview.changes?.filter(c => c.action === 'closed').length || 0}
                </p>
                <p className="text-sm text-slate-400">Closed (sold)</p>
              </div>
              <div className="p-4 bg-slate-700/50 rounded-lg">
                <p className="text-2xl font-bold text-slate-400">
                  {preview.changes?.filter(c => c.action === 'unchanged').length || 0}
                </p>
                <p className="text-sm text-slate-400">Unchanged</p>
              </div>
              <div className="p-4 bg-slate-700/50 rounded-lg">
                <p className="text-2xl font-bold text-slate-200">{preview.accounts_detected?.length || 0}</p>
                <p className="text-sm text-slate-400">Accounts</p>
              </div>
            </div>
          )}

          {preview.changes && preview.changes.length > 0 && (
            <div>
              <button
                onClick={() => setShowChanges(!showChanges)}
                className="flex items-center gap-2 text-slate-300 hover:text-slate-100 transition-colors"
              >
                {showChanges ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                {showChanges ? 'Hide' : 'Show'} Changes ({preview.changes.length})
              </button>
              
              {showChanges && (
                <div className="mt-4 max-h-64 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-700/50">
                      {preview.changes?.[0]?.price !== undefined ? (
                        /* TRADES TABLE HEADER */
                        <tr>
                          <th className="px-3 py-2 text-left">Date</th>
                          <th className="px-3 py-2 text-left">Ticker</th>
                          <th className="px-3 py-2 text-left">Account</th>
                          <th className="px-3 py-2 text-center">Action</th>
                          <th className="px-3 py-2 text-right">Shares</th>
                          <th className="px-3 py-2 text-right">Price</th>
                          <th className="px-3 py-2 text-right">Total</th>
                          <th className="px-3 py-2 text-center">Status</th>
                        </tr>
                      ) : (
                        /* POSITIONS TABLE HEADER */
                        <tr>
                          <th className="px-3 py-2 text-left">Ticker</th>
                          <th className="px-3 py-2 text-left">Account</th>
                          <th className="px-3 py-2 text-right">Old Shares</th>
                          <th className="px-3 py-2 text-right">New Shares</th>
                          <th className="px-3 py-2 text-center">Action</th>
                        </tr>
                      )}
                    </thead>
                    <tbody>
                      {preview.changes.slice(0, 200).map((change, i) => (
                        change.is_contribution ? (
                          /* CONTRIBUTION ROW */
                          <tr key={i} className={`border-b border-slate-700/50 bg-emerald-900/20 ${change.status === 'duplicate' ? 'opacity-50' : ''}`}>
                            <td className="px-3 py-2 text-slate-400">{change.date}</td>
                            <td className="px-3 py-2 font-medium">💰 {change.ticker}</td>
                            <td className="px-3 py-2 text-slate-400">{change.account}</td>
                            <td className="px-3 py-2 text-center">
                              <span className={`badge ${change.action === 'deposit' ? 'badge-success' : 'badge-danger'}`}>
                                {change.action}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-right font-mono" colSpan="2">
                              <span className={change.amount > 0 ? 'text-emerald-400' : 'text-red-400'}>
                                {change.amount > 0 ? '+' : ''}{change.amount?.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-right font-mono text-xs text-slate-500" title={change.source}>
                              {change.source?.slice(0, 20)}...
                            </td>
                            <td className="px-3 py-2 text-center">
                              <span className={`badge ${change.status === 'duplicate' ? 'bg-slate-600 text-slate-300' : 'badge-success'}`}>
                                {change.status === 'duplicate' ? 'skip' : 'new'}
                              </span>
                            </td>
                          </tr>
                        ) : preview.changes?.[0]?.price !== undefined || change.price !== undefined ? (
                          /* TRADES TABLE ROW */
                          <tr key={i} className={`border-b border-slate-700/50 ${change.status === 'duplicate' ? 'opacity-50' : ''}`}>
                            <td className="px-3 py-2 text-slate-400">{change.date}</td>
                            <td className="px-3 py-2 font-medium">{change.ticker}</td>
                            <td className="px-3 py-2 text-slate-400">{change.account}</td>
                            <td className="px-3 py-2 text-center">
                              <span className={`badge ${change.action === 'BUY' ? 'badge-success' : 'badge-danger'}`}>
                                {change.action}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-right font-mono">{change.shares?.toFixed(2)}</td>
                            <td className="px-3 py-2 text-right font-mono">${change.price?.toFixed(2)}</td>
                            <td className="px-3 py-2 text-right font-mono">${change.total?.toFixed(2)}</td>
                            <td className="px-3 py-2 text-center">
                              <span className={`badge ${change.status === 'duplicate' ? 'bg-slate-600 text-slate-300' : 'badge-success'}`}>
                                {change.status === 'duplicate' ? 'skip' : 'new'}
                              </span>
                            </td>
                          </tr>
                        ) : (
                          /* POSITIONS TABLE ROW */
                          <tr key={i} className="border-b border-slate-700/50">
                            <td className="px-3 py-2 font-medium">{change.ticker}</td>
                            <td className="px-3 py-2 text-slate-400">{change.account}</td>
                            <td className="px-3 py-2 text-right font-mono">{change.old_shares?.toFixed(2) || '0'}</td>
                            <td className="px-3 py-2 text-right font-mono">{change.new_shares?.toFixed(2) || change.shares?.toFixed(2) || '0'}</td>
                            <td className="px-3 py-2 text-center">
                              <span className={`badge ${
                                change.action === 'create' ? 'badge-success' : 
                                change.action === 'closed' ? 'badge-danger' :
                                change.action === 'unchanged' ? 'bg-slate-600 text-slate-300' :
                                'badge-info'
                              }`}>
                                {change.action || 'update'}
                              </span>
                            </td>
                          </tr>
                        )
                      ))}
                    </tbody>
                  </table>
                  {preview.changes.length > 200 && (
                    <p className="text-sm text-slate-400 mt-2 px-3">
                      Showing first 200 of {preview.changes.length} changes
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Result */}
      {result && (
        <div className={`p-4 rounded-xl border ${
          result.status === 'success'
            ? 'bg-emerald-500/20 border-emerald-500/30'
            : 'bg-red-500/20 border-red-500/30'
        }`}>
          <div className="flex items-start gap-3">
            {result.status === 'success' ? (
              <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
            ) : (
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            )}
            <div className="flex-1">
              <p className={`font-medium ${result.status === 'success' ? 'text-emerald-400' : 'text-red-400'}`}>
                {result.status === 'success' ? 'Import Successful!' : 'Import Failed'}
              </p>
              <p className="text-sm text-slate-300 mt-1">
                {result.positions_updated > 0 && `${result.positions_updated} positions updated. `}
                {result.trades_logged > 0 && `${result.trades_logged} trades logged. `}
                {result.contributions_logged > 0 && `${result.contributions_logged} contributions recorded. `}
                {result.message && result.message}
              </p>
              
              {/* Realized Gains Result Details */}
              {result.auto_closed && result.auto_closed.length > 0 && (
                <div className="mt-3 space-y-2">
                  <p className="text-sm font-medium text-emerald-300">Lots Closed & Gains Recorded:</p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-slate-400">
                          <th className="text-left p-1">Ticker</th>
                          <th className="text-right p-1">Shares</th>
                          <th className="text-right p-1">Proceeds</th>
                          <th className="text-right p-1">Realized G/L</th>
                        </tr>
                      </thead>
                      <tbody>
                        {result.auto_closed.map((lot, i) => (
                          <tr key={i} className="border-t border-slate-700/50">
                            <td className="p-1 font-medium text-white">{lot.ticker}</td>
                            <td className="p-1 text-right text-slate-300">{lot.shares_closed?.toFixed(3)}</td>
                            <td className="p-1 text-right text-slate-300">${lot.proceeds?.toFixed(2)}</td>
                            <td className={`p-1 text-right font-medium ${lot.realized_gain >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                              {lot.realized_gain >= 0 ? '+' : ''}${lot.realized_gain?.toFixed(2)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              
              {result.errors && result.errors.length > 0 && (
                <ul className="text-sm text-red-300 mt-2 list-disc list-inside">
                  {result.errors.map((err, i) => (
                    <li key={i}>{err}</li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

